/**
 * The record the "Report this fingering" testing aid copies: a phrase as the
 * lesson fingered and drew it, with what the runtime was given to finger it
 * from, so the same fingering can be worked out again elsewhere.
 */
import type { Hand, HandState, Key } from 'ErgoBot';
import type { PrerenderedPhraseData } from 'Types';
import type { ParallelModeConversion } from 'Utils/MusicXmlModeConversion';
import { fingerNumberToDraw, type FingeringDisplay } from './display';
import { handLinesOf, lineNotesOf, midiOf } from './handLines';
import type { HandStates } from './phraseFingering';

export const FINGERING_REPORT_FORMAT = 'ergobot-fingering-report';
export const FINGERING_REPORT_VERSION = 1;

type PrerenderedNote = PrerenderedPhraseData['currentVoiceEntries'][number]['Notes'][number];

/**
 * One hand of a phrase. Every list has an entry per note and rest of the hand's
 * line, in time order; a rest's entry is null.
 */
export interface HandFingeringReport {
  /** The notes as written: the pitch names the runtime was given. */
  written: Array<string | null>;
  /** The keys the notes sound on, as MIDI numbers. */
  soundingMidi: Array<number | null>;
  /** The finger computed for each note; null too for a note that got none. */
  fingers: Array<number | null>;
  /** How much each number is needed, 0 to 100. */
  need: Array<number | null>;
  /** The numbers drawn at the density in force; null where none is. */
  shown: Array<number | null>;
  /** False when the tier does not display this hand's numbers at any density:
   *  the hand is fingered for its state, and `fingers` holds nothing. */
  numbersShown: boolean;
  /** Where the hand was when the phrase began: what it was fingered from. */
  from: HandState | null;
  /** Where the phrase leaves the hand. */
  to: HandState | null;
}

export interface PhraseFingeringReport {
  phraseId: string;
  level: number | null;
  tier: number | null;
  /** The tier of the tables the phrase was fingered on; null when they have none. */
  tierFolder: string | null;
  /** True when selection said the tier held no phrase the hands can get into. */
  joinFallback: boolean;
  /** What the written notes sound as, the way the runtime was told. */
  key: Key;
  /** The key signature the phrase is drawn with, as the lesson names it. */
  keyLabel: string | null;
  /** Null for a hand whose staff is not one line. */
  hands: Record<Hand, HandFingeringReport | null>;
}

/** What the scene knows about the phrase the cursor is on and its neighbours. */
export interface SightReadingFingeringReport {
  tablesVersion: string;
  contractVersion: string;
  density: FingeringDisplay;
  /** The lowest need score drawn at that density; null when no number is. */
  minimumNeed: number | null;
  transposition: number;
  modeConversion: ParallelModeConversion;
  /** The phrase the cursor is on. */
  phrase: PhraseFingeringReport;
  /** The other phrases on screen; `position` counts from the cursor's phrase,
   *  -1 for the one played before it and 1 for the one after. */
  otherPhrasesOnScreen: Array<PhraseFingeringReport & { position: number }>;
}

/** A phrase as the lesson holds it once it is fingered. */
export interface FingeredPhrase {
  phraseId: string;
  levelTier: { level: number; tier: number } | undefined;
  tierFolder: string | null;
  joinFallback: boolean;
  key: Key;
  keyLabel: string | null;
  /** The phrase's notes as its file has them. */
  written: PrerenderedPhraseData[];
  /** The same notes as they are drawn: sounding, with their computed numbers. */
  drawn: PrerenderedPhraseData[];
  entering: HandStates;
  leaving: HandStates;
  /** Per hand, whether the tier displays its finger numbers at all. */
  numbersShown: Record<Hand, boolean>;
}

const HANDS: ReadonlyArray<Hand> = ['rh', 'lh'];

/** The finger computed for a drawn note. A number without a need score came
 *  with the phrase file and is not one of the runtime's. */
const computedFingerOf = (note: PrerenderedNote): number | null =>
  note.isRest || note.fingering === undefined || note.fingeringNeed === undefined
    ? null
    : Number(note.fingering.value);

const shownFingerOf = (note: PrerenderedNote, minimumNeed: number): number | null => {
  const drawn = note.isRest
    ? undefined
    : fingerNumberToDraw(note.fingering, note.fingeringNeed, minimumNeed);
  return drawn === undefined ? null : Number(drawn);
};

const handReportOf = (
  fingered: FingeredPhrase,
  hand: Hand,
  minimumNeed: number,
): HandFingeringReport | null => {
  const line = handLinesOf(fingered.written)[hand];
  const drawnNotes = lineNotesOf(fingered.drawn, hand);
  if (line === null || drawnNotes === null) return null;
  return {
    written: [...line.line],
    soundingMidi: drawnNotes.map((note) => (note.isRest ? null : midiOf(note.pitch))),
    fingers: drawnNotes.map(computedFingerOf),
    need: drawnNotes.map((note) =>
      computedFingerOf(note) === null ? null : (note.fingeringNeed ?? null),
    ),
    shown: drawnNotes.map((note) => shownFingerOf(note, minimumNeed)),
    numbersShown: fingered.numbersShown[hand],
    from: fingered.entering[hand],
    to: fingered.leaving[hand],
  };
};

/** One phrase of the record, its numbers drawn from a need score of `minimumNeed` up. */
export const phraseFingeringReportOf = (
  fingered: FingeredPhrase,
  minimumNeed: number,
): PhraseFingeringReport => ({
  phraseId: fingered.phraseId,
  level: fingered.levelTier?.level ?? null,
  tier: fingered.levelTier?.tier ?? null,
  tierFolder: fingered.tierFolder,
  joinFallback: fingered.joinFallback,
  key: fingered.key,
  keyLabel: fingered.keyLabel,
  hands: HANDS.reduce<Record<Hand, HandFingeringReport | null>>(
    (hands, hand) => ({ ...hands, [hand]: handReportOf(fingered, hand, minimumNeed) }),
    { rh: null, lh: null },
  ),
});

/** Where a report was made: enough to tell a phone from a laptop. */
export interface ReportingDevice {
  userAgent: string;
  viewport: { width: number; height: number };
}

export interface FingeringReportRecord extends SightReadingFingeringReport {
  format: typeof FINGERING_REPORT_FORMAT;
  version: typeof FINGERING_REPORT_VERSION;
  /** When the report was made, as an ISO 8601 time. */
  reportedAt: string;
  device: ReportingDevice;
}

/** The whole record: what the scene reported, stamped with when and where. */
export const fingeringReportRecordOf = (
  reported: SightReadingFingeringReport,
  reportedAt: Date,
  device: ReportingDevice,
): FingeringReportRecord => ({
  format: FINGERING_REPORT_FORMAT,
  version: FINGERING_REPORT_VERSION,
  reportedAt: reportedAt.toISOString(),
  ...reported,
  device,
});

/** A few words that say which phrase a record is of, for the confirmation. */
export const fingeringReportLabelOf = ({ phrase }: SightReadingFingeringReport): string => {
  const tier =
    phrase.level === null || phrase.tier === null ? '' : `L${phrase.level} T${phrase.tier}, `;
  return `${tier}phrase ${phrase.phraseId}`;
};
